import { RefObject, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@musinsa/mwms-ds'
import styled, { css } from 'styled-components'

import useClickOutside from '@/hooks/useClickOutside'
import useModal from '@/hooks/useModal'
import { colors, colorSystem } from '@/styles/theme'
import Text from '@/components/content/Text'
import RemixIcon from '@/components/content/RemixIcon'
import Tooltip from '@/components/content/Tooltip'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { AllocationStrategy, SlotView } from '@/services/outboundSchedule2/types'

import { formatWaitTime } from '../../utils/formatWaitTime'
import { BaseCell } from '../shared/BaseCell'
import { useIsIn15MinCutOff } from '../../hooks/useIsIn15MinCutOff'
import { useOptionsPosition } from '../../hooks/useOptionsPosition'
import { useCutOffDateTimeContext } from '../../context/CutOffRowDateTimeContext'
import { useCutOffHoverContext } from '../../context/CutOffHoverContext'
import { WAIT_TIME_EDIT_ALLOWED_EMAILS } from '../../constants'
import { AllocationPriorityModal } from './AllocationPriorityModal'
import { SetWaitTimeModal } from './SetWaitTimeModal'

type Props = {
  scrollContainerRef: RefObject<HTMLDivElement>
  hasSchedules: boolean
  schedule?: SlotView
  waitTime?: number
  isCurrent?: boolean
  isExpired?: boolean
}

type Position = { top: number; left: number } | undefined

const AllocationStrategyMap: Record<AllocationStrategy, string> = {
  PACKING: '합포',
  WAREHOUSE_PRIORITY: '센터',
}

export function CutOffCutTypeCell({ scrollContainerRef, hasSchedules, schedule, waitTime, isCurrent = false, isExpired = false }: Props) {
  const [optionsPosition, setOptionsPosition] = useState<Position>()
  const [isOpen, setIsOpen] = useState(false)

  const { date, time } = useCutOffDateTimeContext()
  const { isCutOffHover } = useCutOffHoverContext()

  const isIn15MinCutOff = useIsIn15MinCutOff({ date, time })

  const priorityModal = useModal()
  const waitTimeModal = useModal()

  const wrapRef = useRef<HTMLDivElement>(null)
  useClickOutside(wrapRef, () => setIsOpen(false), !priorityModal.open && !waitTimeModal.open) // 내부 모달이 열려있을떄는 enabled처리

  const iconColor = isCurrent ? colors.white : isExpired ? colors.gray500 : 'rgba(0, 0, 0, 0.88)'
  const moreIconColor: 'white' | 'gray500' | 'gray800' = isCurrent ? 'white' : isExpired ? 'gray500' : 'gray800'
  const backgroundColor = isCurrent ? 'transparent' : isCutOffHover ? 'rgba(36, 94, 255, 0.04)' : 'transparent'

  useOptionsPosition({ isIn15MinCutOff, targetRef: wrapRef, setPosition: setOptionsPosition, scrollContainerRef })

  const isInteractive = hasSchedules && !isExpired && !isCurrent

  const cellContent = (
    <TypeBoxWrap
      $isOpen={isOpen}
      $isInteractive={isInteractive}
      onClick={() => {
        if (!isInteractive) return
        setIsOpen(true)
      }}
      ref={wrapRef}
      style={{ backgroundColor }}
    >
      <LabelTag $isCurrent={isCurrent}>
        <RemixIcon name="ri-time-line" size={16} color={iconColor} style={{ lineHeight: 1, display: 'block' }} />
        <LabelTagText style={{ color: iconColor }}>{formatWaitTime(waitTime ?? 60)}</LabelTagText>
      </LabelTag>

      {hasSchedules && schedule?.priorityType && (
        <Text
          className="allocation-strategy-text"
          kind="Body_12_regular"
          color={isCurrent ? colors.white : isExpired ? colorSystem.text.common.tertiary : colorSystem.text.common.tertiary}
        >
          {AllocationStrategyMap[schedule.priorityType]}
        </Text>
      )}
      {isInteractive && <Icon className="more-icon" name="menu" size={16} color={moreIconColor} />}
      {isOpen && schedule && (
        <CutOffOptions
          position={optionsPosition}
          priorityModal={priorityModal}
          waitTimeModal={waitTimeModal}
          schedule={schedule}
          waitTime={waitTime}
          onCloseMenu={() => setIsOpen(false)}
        />
      )}
    </TypeBoxWrap>
  )

  if (isCurrent) {
    return <Tooltip label="운영중인 시간에는 변경이 불가합니다.">{cellContent}</Tooltip>
  }

  return cellContent
}

const TypeBoxWrap = styled(BaseCell)<{ $isOpen: boolean; $isInteractive: boolean }>`
  width: var(--wait-time-cell-width);
  position: relative;
  background-color: transparent;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'pointer' : 'default')};

  justify-content: space-between;

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          .more-icon {
            display: block;
          }
          .allocation-strategy-text {
            display: none;
          }
        `
      : css`
          .more-icon {
            display: none;
          }
        `}

  ${({ $isInteractive }) =>
    $isInteractive &&
    css`
      &:hover {
        .more-icon {
          display: block;
        }
        .allocation-strategy-text {
          display: none;
        }
      }
    `}
`

const LabelTag = styled.div<{ $isCurrent: boolean }>`
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  background-color: ${({ $isCurrent }) => ($isCurrent ? 'transparent' : colors.white)};

  ${({ $isCurrent }) =>
    !$isCurrent &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
        pointer-events: none;
      }
    `}
`

const LabelTagText = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  position: relative;
  z-index: 1;
`

function CutOffOptions({
  position,
  priorityModal,
  waitTimeModal,
  schedule,
  waitTime,
  onCloseMenu,
}: {
  position: Position
  priorityModal: ReturnType<typeof useModal>
  waitTimeModal: ReturnType<typeof useModal>
  schedule: SlotView
  waitTime?: number
  onCloseMenu: () => void
}) {
  const { time } = useCutOffDateTimeContext()

  // sessionStorage의 email은 탭 세션 내 불변이므로 [] 의존성으로 충분
  const canEditWaitTime = useMemo(() => {
    const email = AuthStorage.user.get().email
    return !!email && WAIT_TIME_EDIT_ALLOWED_EMAILS.includes(email)
  }, [])

  const optionsContent = (
    <>
      <Options $width={160} style={{ ...position }}>
        <OptionItem
          onClick={(e) => {
            e.stopPropagation()
            priorityModal.onOpenModal()
          }}
        >
          우선순위 설정
        </OptionItem>
        {canEditWaitTime && (
          <OptionItem
            onClick={(e) => {
              e.stopPropagation()
              waitTimeModal.onOpenModal()
            }}
          >
            출고대기시간 변경
          </OptionItem>
        )}
      </Options>
      {priorityModal.open && (
        <AllocationPriorityModal time={time} schedule={schedule} onClose={priorityModal.onCloseModal} onSuccess={onCloseMenu} />
      )}
      {waitTimeModal.open && (
        <SetWaitTimeModal time={time} waitTime={waitTime} schedule={schedule} onClose={waitTimeModal.onCloseModal} onSuccess={onCloseMenu} />
      )}
    </>
  )

  return position ? createPortal(optionsContent, document.body) : null
}

const Options = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${colors.white};
  border: 1px solid ${colorSystem.border.element};
  z-index: 99;

  position: absolute;

  display: flex;
  flex-direction: column;
  gap: 8px;
`

const OptionItem = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  cursor: pointer;

  &:hover {
    background-color: ${colors.gray050};
  }
`
